import { Trip, Apartment, Traveller, SkiPass, ApartmentType, TransportationOption } from '@/types';
import * as tripRepository from '@/repository/tripRepository';
import { Unsubscribe } from 'firebase/firestore';
import { calculateDuplicationStatistics, resetApartmentOccupancy, prepareTripDataForDuplication } from '@/utils/duplicationUtils';

// --- Echtzeit-Listener --- //

/**
 * Richtet einen Echtzeit-Listener für die gesamte Trips-Sammlung ein.
 * @param callback Die Funktion, die bei jeder Datenaktualisierung aufgerufen wird.
 * @param filters Ein optionales Array von Filtern, die auf die Abfrage angewendet werden sollen.
 * @returns Eine `unsubscribe`-Funktion, um den Listener zu entfernen.
 */
export const listenToTrips = (callback: (trips: Trip[]) => void, filters: tripRepository.TripFilter[] = []): Unsubscribe => {
  return tripRepository.listenToTrips(callback, filters);
};

/**
 * Richtet einen Echtzeit-Listener für eine einzelne Reise ein.
 * @param tripId Die ID der zu beobachtenden Reise.
 * @param callback Die Funktion, die bei jeder Datenaktualisierung aufgerufen wird.
 * @returns Eine `unsubscribe`-Funktion, um den Listener zu entfernen.
 */
export const listenToTripById = (tripId: string, callback: (trip: Trip | undefined) => void): Unsubscribe => {
  return tripRepository.listenToTripById(tripId, callback);
};

// --- Einmalige Datenabrufe --- //

export const getTripById = async (tripId: string): Promise<Trip | undefined> => {
  return tripRepository.getTripById(tripId);
};

export const getTrips = async (): Promise<Trip[]> => {
  return tripRepository.getTrips();
};

// --- Schreiboperationen --- //

export const addTrip = async (newTripData: Omit<Trip, 'id'>): Promise<string> => {
  // Hier könnte Geschäftslogik stehen (Validierung, Standardwerte etc.)
  return tripRepository.addTrip(newTripData);
};

export const updateTrip = async (updatedTrip: Trip): Promise<void> => {
  return tripRepository.updateTrip(updatedTrip);
};

export const deleteTrip = async (tripId: string): Promise<void> => {
    return tripRepository.deleteTrip(tripId);
}

export const duplicateTrip = async (
    sourceTripId: string,
    newTripName: string,
    options: {
        includeApartments: boolean;
        includeTravellers: boolean;
        includeSkiPasses: boolean;
        includeApartmentTypes: boolean;
        includeTransportation: boolean;
    }
  ): Promise<Trip> => {
    const sourceTrip = await tripRepository.getTripById(sourceTripId);
    if (!sourceTrip) {
      throw new Error("Quell-Trip nicht gefunden");
    }

    // Get apartments and travellers from subcollections if needed
    const apartments = options.includeApartments ? await tripRepository.getSubcollection<Apartment>(sourceTripId, 'apartments') : [];
    const travellers = options.includeTravellers ? await tripRepository.getSubcollection<Traveller>(sourceTripId, 'travellers') : [];

    // Prepare trip data with new IDs for sub-objects
    const { skiPasses, apartmentTypes, transportationOptions } = prepareTripDataForDuplication(sourceTrip, {
      includeSkiPasses: options.includeSkiPasses,
      includeApartmentTypes: options.includeApartmentTypes,
      includeTransportation: options.includeTransportation
    });

    // Calculate proper statistics based on what's being included
    const statistics = calculateDuplicationStatistics(
      {
        includeApartments: options.includeApartments,
        includeTravellers: options.includeTravellers,
        includeSkiPasses: options.includeSkiPasses
      },
      sourceTrip,
      apartments,
      travellers
    );

    // Reset apartment occupancy if travellers are not included
    const processedApartments = !options.includeTravellers ? resetApartmentOccupancy(apartments) : apartments;

    const { id, ...baseTripData } = sourceTrip;

    const newTripData: Omit<Trip, 'id'> = {
      ...baseTripData,
      name: newTripName,
      statistics,
      skiPasses,
      apartmentTypes,
      transportationOptions,
    };

    const newTripId = await tripRepository.addTrip(newTripData);

    // Add apartments and travellers to subcollections with new IDs
    if (options.includeApartments && processedApartments.length > 0) {
        await tripRepository.addItemsToSubcollection(newTripId, 'apartments', processedApartments);
    }
    if (options.includeTravellers && travellers.length > 0) {
        await tripRepository.addItemsToSubcollection(newTripId, 'travellers', travellers);
    }

    const newTrip = await tripRepository.getTripById(newTripId);
    if (!newTrip) {
        throw new Error("Konnte den neu erstellten Trip nicht abrufen.");
    }

    return newTrip;
  };
